import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import PropTypes from 'prop-types';

import { resendRelayFax } from 'store/thunks/workQueueThunks';

import { relayFaxDestination } from '../constants/nonRoutableRequests';
import { useNonRoutableResolution } from '../hooks/useNonRoutableResolution';
import { nonRoutableRetryIsSafe } from '../utils/nonRoutableRequestErrors';
import NonRoutableRequestIdentity from './NonRoutableRequestIdentity';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';
import RelayFaxPhoneConfirmation from './RelayFaxPhoneConfirmation';

const CONFIRMATION_REQUIRED = 'RELAY_FAX_RESEND_CONFIRMATION_REQUIRED';

// Keyed on RelayFaxActionOutcome. Anything else still says something true.
const RESEND_SUCCESS = {
  RESEND_QUEUED:
    'Fax queued to resend. The request stays In Progress until the fax service reports the result.',
  DELIVERED:
    'The fax had already gone through, so nothing was resent. The order is marked delivered and ' +
    'now appears in the Relay tab.',
};

const successMessage = (_detail, result) =>
  RESEND_SUCCESS[result?.outcome] ??
  'The resend finished. Refresh the queue to see where the request is now.';

/**
 * RESEND A PARKED RELAY FAX (POM-6279).
 *
 * A FAX CANNOT BE RECALLED, so the server decides by the fax code: nothing transmitted, or a
 * delivery the fax service says failed, is simply re-queued; an UNKNOWN outcome is first checked with
 * the fax service, and when that cannot settle it the server refuses with
 * RELAY_FAX_RESEND_CONFIRMATION_REQUIRED. That refusal is not shown as an error — it becomes this
 * dialog's second step: phone the pharmacy, and resend only if it did NOT receive the fax. The
 * second call carries `confirmResend: true`; the first never does.
 */
const ResendRelayFaxDialog = ({ open, onClose, item }) => {
  const [phoned, setPhoned] = useState(false);
  const { submit, submitting, outcome, dismiss } = useNonRoutableResolution({
    item,
    action: resendRelayFax,
    successMessage,
    onClose,
    open,
  });

  const needsPhoneConfirmation = outcome?.described.code === CONFIRMATION_REQUIRED;

  // The tick answers ONE confirmation request. Reopening the dialog must ask again, and so must
  // every NEW request from the server — e.g. a confirmed resend that was rolled back and then
  // retried — which is why `handleResend` clears it as each attempt is sent.
  useEffect(() => {
    if (open) setPhoned(false);
  }, [open]);
  const pharmacy = relayFaxDestination(item);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    dismiss();
  };

  const canSubmit = !outcome || needsPhoneConfirmation || nonRoutableRetryIsSafe(outcome.described);

  const handleResend = () => {
    const confirmResend = needsPhoneConfirmation && phoned;
    setPhoned(false);
    return confirmResend ? submit({ confirmResend: true }) : submit();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='ResendRelayFaxDialog-title'
    >
      <DialogTitle id='ResendRelayFaxDialog-title'>Resend this fax?</DialogTitle>
      <DialogContent>
        <NonRoutableRequestIdentity item={item} />

        {needsPhoneConfirmation ? (
          <Alert severity='warning' data-testid='relay-fax-resend-confirmation'>
            <AlertTitle>Phone the pharmacy before resending</AlertTitle>
            {`We cannot tell whether the last fax reached ${pharmacy}. Phone them now. Resend only ` +
              'if they confirm they did NOT receive it. If they did receive it, close this and ' +
              'use Reconcile Fax instead.'}
            <RelayFaxPhoneConfirmation
              id={`ResendRelayFaxDialog-${item.nonRoutableRequestId}-phoned`}
              checked={phoned}
              onChange={setPhoned}
              label='I phoned the pharmacy and they did NOT receive this fax.'
            />
          </Alert>
        ) : (
          <Alert severity='info'>
            <AlertTitle>A fax cannot be recalled</AlertTitle>
            {`The fax is sent to ${pharmacy} again. If the last attempt may have reached them, ` +
              'you will be asked to phone them before anything is resent.'}
          </Alert>
        )}

        {outcome && !needsPhoneConfirmation && <NonRoutableResolutionOutcome outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={dismiss} disabled={submitting}>
          {outcome && !canSubmit ? 'Close' : 'Leave it parked'}
        </Button>
        {canSubmit && (
          <LoadingButton
            variant='contained'
            color='primary'
            loading={submitting}
            disabled={needsPhoneConfirmation && !phoned}
            onClick={handleResend}
            id={`ResendRelayFaxDialog-${item.nonRoutableRequestId}-confirm`}
          >
            Resend Fax
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

ResendRelayFaxDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    destinationName: PropTypes.string,
  }).isRequired,
};

export default ResendRelayFaxDialog;
